import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", rep_code: "", active: true, weight: 1 };

export default function AdminReps() {
  const [reps, setReps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setReps((await api.get("/admin/reps")).data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (r) => { setEditing(r.id); setForm({ name: r.name, phone: r.phone, rep_code: r.rep_code, active: r.active, weight: r.weight }); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/admin/reps", form);
        toast.success("Rep created");
      } else {
        await api.patch(`/admin/reps/${editing}`, form);
        toast.success("Rep updated");
      }
      cancel();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this rep?")) return;
    await api.delete(`/admin/reps/${id}`);
    toast.success("Rep removed");
    load();
  };

  const toggleActive = async (r) => {
    await api.patch(`/admin/reps/${r.id}`, { active: !r.active });
    load();
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">Round-robin pool</p>
          <h1 className="editorial-h text-5xl text-brand-dark mt-2">WhatsApp Reps</h1>
          <p className="font-sans text-sm text-brand-muted mt-2">Inactive reps are skipped. New ones join the rotation instantly.</p>
        </div>
        <Button onClick={startNew} data-testid="rep-new-btn" className="rounded-none bg-brand-primary hover:bg-brand-primaryHover text-brand-bone gap-2">
          <Plus strokeWidth={1.5} className="w-4 h-4" /> Add rep
        </Button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-8 bg-white border border-brand-line p-6 grid sm:grid-cols-2 gap-4" data-testid="rep-form">
          <div>
            <Label className="overline">Display name</Label>
            <Input className="mt-2 h-11 rounded-none border-brand-line" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rep-form-name" required />
          </div>
          <div>
            <Label className="overline">WhatsApp number (intl, no +)</Label>
            <Input className="mt-2 h-11 rounded-none border-brand-line" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, "") })} placeholder="919999996362" data-testid="rep-form-phone" required />
          </div>
          <div>
            <Label className="overline">Unique rep code (for ?to=)</Label>
            <Input className="mt-2 h-11 rounded-none border-brand-line" value={form.rep_code} onChange={(e) => setForm({ ...form, rep_code: e.target.value })} placeholder="elvis-6362" data-testid="rep-form-code" required />
          </div>
          <div>
            <Label className="overline">Weight</Label>
            <Input type="number" min="1" max="20" className="mt-2 h-11 rounded-none border-brand-line" value={form.weight} onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })} data-testid="rep-form-weight" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="rep-form-active" />
            <Label className="font-sans text-sm">Active</Label>
          </div>
          <div className="sm:col-span-2 flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={cancel} className="rounded-none border-brand-line" data-testid="rep-form-cancel">Cancel</Button>
            <Button type="submit" className="rounded-none bg-brand-primary text-brand-bone" data-testid="rep-form-submit">Save</Button>
          </div>
        </form>
      )}

      <div className="mt-8 bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-elevated">
            <tr className="text-left">
              <th className="px-4 py-3 overline text-brand-muted">Name</th>
              <th className="px-4 py-3 overline text-brand-muted">Phone</th>
              <th className="px-4 py-3 overline text-brand-muted">Rep code</th>
              <th className="px-4 py-3 overline text-brand-muted">Weight</th>
              <th className="px-4 py-3 overline text-brand-muted">Active</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="p-6 text-center text-brand-muted">Loading…</td></tr>}
            {reps.map((r) => (
              <tr key={r.id} className="border-t border-brand-line" data-testid={`rep-row-${r.rep_code}`}>
                <td className="px-4 py-3 font-semibold">{r.name}</td>
                <td className="px-4 py-3 font-mono text-xs">+{r.phone}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.rep_code}</td>
                <td className="px-4 py-3">{r.weight}</td>
                <td className="px-4 py-3">
                  <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`rep-toggle-${r.rep_code}`} />
                </td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => startEdit(r)} className="text-brand-dark hover:text-brand-primary" data-testid={`rep-edit-${r.rep_code}`}><Pencil strokeWidth={1.5} className="w-4 h-4" /></button>
                  <button onClick={() => remove(r.id)} className="text-brand-dark hover:text-brand-primary" data-testid={`rep-delete-${r.rep_code}`}><Trash2 strokeWidth={1.5} className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
